import { ItiLink as Link } from 'components';
import { useAuth } from 'contexts';
import { SelectedLocationContext } from 'contexts/selected-location';
import { FC, PropsWithChildren, useContext, useState } from 'react';
import { Link as ReactRouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Role } from 'types';
import './nav.scss';

import avatarimg from 'assets/images/CMC_Logo.png';

type ItemProps = PropsWithChildren<{ text: string; to: string }> & { [P in Role]?: boolean };
const Item: FC<ItemProps> = ({ children, text, to, ...authorizedRoles }) => {
  const { pathname } = useLocation();
  const { userProfile } = useAuth();

  return !authorizedRoles[userProfile.role] ? null : (
    <Link className={`d-flex align-items-center g-3 p-3 selected-${pathname.includes('/' + to)}`} to={to}>
      <span className="material-icons icon-large">{children}</span>
      <span className="font-weight-bold">{text}</span>
    </Link>
  );
};

const Nav = () => {
  const username = localStorage.getItem('username');
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { selectedLocation, setSelectedLocationById, locations } = useContext(SelectedLocationContext);
  const [open, setOpen] = useState(window.innerWidth > 1440);

  return (
    <nav className={`nav-component d-flex flex-column justify-content-between open-${open}`}>
      <div className="d-flex flex-column align-items-center w-100">
        <div className="nav-logo">
          <img alt="avatar" className="avatar" height={56} src={avatarimg} />
          <div className="welcome">
            <p>Welcome back, </p>
            <h4 className="text-secondary font-weight-medium text-capitalize m-0">{username}</h4>
          </div>
        </div>
        {/* Location selection */}
        {userProfile.role !== Role.BOOKING &&
          userProfile.role !== Role.GATE &&
          userProfile.role !== Role.GATE_BOOKING && (
            <select
              className="nav-select"
              value={selectedLocation?.id}
              onChange={e => setSelectedLocationById(Number(e.target.value))}
            >
              {locations.map(l => (
                <option key={l.id} value={l.id}>
                  {l.name}&nbsp;({l.state?.slice(0, 2).toUpperCase()})
                </option>
              ))}
            </select>
          )}
      </div>
      <div className="d-flex flex-column g-1 p-3 overflow-auto">
        <Item
          text="Dashboard"
          to="dashboard"
          ADMIN
          BILLING_ADMIN
          BOOKING
          SENIOR_BOOKING
          ADMIN_CUSTOMER
          CUSTOMER
          CARRIER
          EXTERNAL
        >
          dashboard
        </Item>
        <Item text="Gate" to="gate" ADMIN BILLING_ADMIN SENIOR_BOOKING BOOKING GATE GATE_BOOKING>
          local_shipping
        </Item>
        <Item
          text="Transactions"
          to="transactions"
          ADMIN
          BILLING_ADMIN
          SENIOR_BOOKING
          BOOKING
          GATE_BOOKING
          ADMIN_CUSTOMER
          CUSTOMER
          CARRIER
          EXTERNAL
        >
          receipt
        </Item>
        <Item
          text="Yard Management"
          to="units"
          ADMIN
          BILLING_ADMIN
          SENIOR_BOOKING
          BOOKING
          GATE_BOOKING
          ADMIN_CUSTOMER
          CUSTOMER
          EXTERNAL
        >
          grid_view
        </Item>
        <Item text="Stop / Allow" to="orders" ADMIN BILLING_ADMIN SENIOR_BOOKING BOOKING>
          disabled_by_default
        </Item>
        <Item
          text="Bookings"
          to="bookings"
          ADMIN
          BILLING_ADMIN
          SENIOR_BOOKING
          BOOKING
          GATE_BOOKING
          ADMIN_CUSTOMER
          CUSTOMER
          EXTERNAL
        >
          collections_bookmark
        </Item>
        <Item
          text="Redeliveries"
          to="redeliveries"
          ADMIN
          BILLING_ADMIN
          SENIOR_BOOKING
          BOOKING
          ADMIN_CUSTOMER
          CUSTOMER
          EXTERNAL
        >
          alt_route
        </Item>
        <Item
          text="Templates"
          to="templates"
          ADMIN
          BILLING_ADMIN
          SENIOR_BOOKING
          BOOKING
          ADMIN_CUSTOMER
          CUSTOMER
          EXTERNAL
        >
          file_copy
        </Item>
        <Item text="Storage Invoicing" to="invoices" ADMIN BILLING_ADMIN SENIOR_BOOKING>
          request_quote
        </Item>
        <Item text="Contracts" to="contracts" ADMIN BILLING_ADMIN>
          history_edu
        </Item>
        <Item
          key={userProfile.role}
          text="SCAC Management"
          to={
            [Role.ADMIN, Role.BILLING_ADMIN, Role.BOOKING, Role.SENIOR_BOOKING, Role.ADMIN_CUSTOMER].includes(
              userProfile.role
            )
              ? 'scac-management'
              : 'scac-management/account-rules'
          }
          ADMIN
          BILLING_ADMIN
          SENIOR_BOOKING
          BOOKING
          ADMIN_CUSTOMER
          CUSTOMER
          EXTERNAL
        >
          local_shipping
        </Item>
        <Item
          text="Settings"
          to="settings"
          ADMIN
          BILLING_ADMIN
          SENIOR_BOOKING
          BOOKING
          ADMIN_CUSTOMER
          CUSTOMER
          CARRIER
          EXTERNAL
        >
          settings
        </Item>
        {userProfile.role === Role.ADMIN ||
        userProfile.role === Role.BILLING_ADMIN ||
        userProfile.role === Role.BOOKING ||
        userProfile.role === Role.SENIOR_BOOKING ? (
          <ReactRouterLink
            className="d-flex align-items-center g-3 p-3"
            to="https://intermodalsolutions.content.us.clicklearn.com/publiccontent/VEM%20Gate.1033/VEM%20Gate.htm?segments=VEM%20Gate%20Users"
          >
            <span className="material-icons icon-large">info</span>
            <span className="font-weight-bold">Internal User Guide</span>
          </ReactRouterLink>
        ) : (
          <ReactRouterLink
            className="d-flex align-items-center g-3 p-3"
            to="https://intermodalsolutions.content.us.clicklearn.com/publiccontent/VEM%20Gate.1033/VEM%20Gate.htm?segments=VEM%20Customer%20Portal"
          >
            <span className="material-icons icon-large">info</span>
            <span className="font-weight-bold">External User Guide</span>
          </ReactRouterLink>
        )}
        <button
          className="a btn-back d-flex align-items-center g-3 p-3"
          onClick={() => navigate('/logout')}
          data-cy="nav-logout"
        >
          <span className="material-icons icon-large">logout</span>
          <span className="font-weight-bold">Logout</span>
        </button>
        <button
          className="a btn-back d-flex justify-content-end p-3"
          onClick={() => setOpen(p => !p)}
          data-cy="nav-chevron"
        >
          <span className="material-icons icon-large">{open ? 'chevron_left' : 'chevron_right'}</span>
        </button>
      </div>
      <div />
    </nav>
  );
};

export default Nav;
